import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import de from "./de.json";
import en from "./en.json";
import pt from "./pt.json";

export type Locale = "EN" | "PT" | "DE";

const STORAGE_KEY = "recspec.locale";

const STRINGS: Record<Locale, Record<string, string>> = {
  EN: en,
  PT: pt,
  DE: de,
};

interface LocaleContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue>({
  locale: "EN",
  setLocale: () => {},
});

function readStoredLocale(): Locale {
  if (typeof window === "undefined") return "EN";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "EN" || raw === "PT" || raw === "DE") return raw;
  return "EN";
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);
  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = locale.toLowerCase();
    }
  }, [locale]);
  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale }),
    [locale, setLocale],
  );
  return createElement(LocaleContext.Provider, { value }, children);
}

/// Returns `(key, params?) => string`. Falls back to EN if a key is
/// missing in the current locale; falls back to the literal key if it's
/// missing in EN too (a "you forgot to register this string" signal).
export function useT(): (key: string, params?: Record<string, string | number>) => string {
  const { locale } = useContext(LocaleContext);
  return useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const raw = STRINGS[locale][key] ?? STRINGS.EN[key] ?? key;
      if (!params) return raw;
      return raw.replace(/\{(\w+)\}/g, (_, name: string) => {
        const v = params[name];
        return v === undefined ? `{${name}}` : String(v);
      });
    },
    [locale],
  );
}

export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}
